import { ChevronLeft, ChevronRight, Download, Pencil } from 'lucide-react';
import * as React from 'react';
import { formatDuration } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Checkbox, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { BulkEditDialog } from '@/components/reporting/bulk-edit-dialog';
import { currentMonthFilter, FilterBar } from '@/components/reporting/filter-bar';
import { TagChips } from '@/components/timesheet/tag-chips';
import { PageContainer } from '@/components/shell/page-container';
import { SavedFilters } from '@/components/reporting/saved-filters';
import { PageHeader } from '@/components/shell/page-header';
import { exportCsvUrl, useEntries, type Filters } from '@/lib/reporting-api';
import type { Session } from '@/lib/session';

const PAGE_SIZE = 50;

/**
 * Every entry matching a filter, newest first.
 *
 * The screen somebody opens to answer "where did these hours go?" - so it shows
 * whole entries with their notes rather than aggregates, and the total in the header
 * is for the WHOLE filter, not the page on screen. A total that silently means
 * "this page" is the kind of number people quote in a meeting and get wrong.
 */
export function AllTimesPage({ session }: { session: Session }) {
  const [filters, setFilters] = React.useState<Filters>(currentMonthFilter);
  const [page, setPage] = React.useState(1);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [editing, setEditing] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const { data, isPending, isError, error } = useEntries(filters, page, PAGE_SIZE);

  // Any filter change returns to the first page: page 7 of a different query is
  // almost always empty, and an empty screen reads as "no data" rather than
  // "wrong page".
  const changeFilters = (next: Filters) => {
    setFilters(next);
    setPage(1);
    // A selection made against the previous filter means nothing against this one.
    setSelected(new Set());
    setNotice(null);
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <PageContainer>
      <PageHeader
        title="All times"
        description="Every entry matching the filter, newest first."
      />

      {selected.size > 0 && (
        <Alert>
          <AlertDescription className="flex flex-wrap items-center gap-3 text-sm">
            <span>
              <strong>{selected.size}</strong> selected
            </span>
            <Button hint={"Edit all the selected entries at once"} size="sm" onClick={() => setEditing(true)}>
              <Pencil className="size-4" />
              Change them
            </Button>
            <Button hint={"Clear the selection"} size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <span className="text-xs text-muted-foreground">
              Selection is per page — page through and it resets.
            </span>
          </AlertDescription>
        </Alert>
      )}

      {notice && (
        <Alert>
          <AlertDescription className="text-sm">{notice}</AlertDescription>
        </Alert>
      )}

      <BulkEditDialog
        ids={[...selected]}
        open={editing}
        onClose={() => setEditing(false)}
        onDone={(updated) => {
          setEditing(false);
          setSelected(new Set());
          setNotice(`${updated} ${updated === 1 ? 'entry' : 'entries'} changed.`);
        }}
      />

      <FilterBar value={filters} onChange={changeFilters} role={session.role}>
        <SavedFilters screen="all-times" value={filters} onChange={changeFilters} />
        <Button hint={"Download the entries matching these filters as a CSV"} asChild variant="outline">
          <a href={exportCsvUrl(filters)}>
            <Download className="size-4" />
            Export CSV
          </a>
        </Button>
      </FilterBar>

      {isError && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">
            {error instanceof Error ? error.message : 'Could not load those entries.'}
          </AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-96 w-full" />}

      {data && (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="ts-num text-sm text-muted-foreground">
              {data.total.toLocaleString('en-IN')} {data.total === 1 ? 'entry' : 'entries'}
            </p>
            <p className="text-sm">
              Total{' '}
              <span className="ts-num text-base font-semibold">
                {formatDuration(data.totalSeconds)}
              </span>
              <span className="ml-1 text-xs text-muted-foreground">across the whole filter</span>
            </p>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table className="min-w-[980px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Person</TableHead>
                      <TableHead>Customer / project</TableHead>
                      <TableHead>Activity</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Checkbox
                            aria-label={`Select the entry on ${row.workDate}`}
                            checked={selected.has(row.id)}
                            onCheckedChange={(v) =>
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (v === true) next.add(row.id);
                                else next.delete(row.id);
                                return next;
                              })
                            }
                          />
                        </TableCell>
                        <TableCell className="ts-num whitespace-nowrap">
                          {shortDate(row.workDate)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">{row.employee.displayName}</TableCell>
                        <TableCell>
                          <div className="text-sm">{row.project.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {row.project.customer.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>{row.activity.name}</div>
                          <TagChips tags={row.tags} />
                        </TableCell>
                        <TableCell className="max-w-[260px] whitespace-pre-wrap text-sm text-muted-foreground">
                          {row.description ?? <span className="opacity-40">—</span>}
                        </TableCell>
                        <TableCell className="ts-num whitespace-nowrap text-right font-medium">
                          {row.durationSeconds === null
                            ? 'running'
                            : formatDuration(row.durationSeconds)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex gap-1">
                            {!row.billable && <Badge variant="outline">not billable</Badge>}
                            {row.exported && <Badge variant="secondary">exported</Badge>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                          Nothing matches that filter.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {pages > 1 && (
            <div className="flex items-center justify-between">
              <span className="ts-num text-sm text-muted-foreground">
                Page {data.page} of {pages}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Previous page"
                  disabled={data.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Next page"
                  disabled={data.page >= pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}

function shortDate(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}
